// The dashboard's organic product table (#512): what sold on this marketplace
// WITHOUT an ad paying for it, over the page's period.
//
// Deliberately narrow. Margin, spend and break-even live on the campaign table
// (`/campaigns/$marketplace`), which answers a bid question; this one answers
// "what sells on its own" and stops there.
//
// The revenue column is Mirakl LINE revenue — tax-excluded, no shipping — so it
// does NOT sum to the Magento GMV headline above the chart. The caption says so
// rather than letting the reader assume a rounding error.

import { Link } from "@tanstack/react-router"
import { ArrowDown, ArrowUp, ArrowUpRight, ExternalLink, Package } from "lucide-react"

import { cn } from "../../lib/utils"
import { CopyValue } from "../ui/copy-value"
import { Money } from "../ui/money"
import { MetricTrend } from "./metric-trend"
import type { ExchangeRates } from "../../lib/money"
import {
  ORGANIC_PRODUCT_SORTS,
  type OrganicProductOrdering,
  type OrganicProductRow,
  type OrganicProductSort,
  type OrganicProductSortDirection,
  type OrganicProductsResponse,
} from "../../services/use-organic-products"

export function OrganicProductsTable({
  data,
  currency,
  rates,
  comparisonLabel,
  ordering,
  onOrderingChange,
}: {
  data: OrganicProductsResponse | undefined
  currency: string
  rates: ExchangeRates | undefined
  /** Tooltip context for the trend chips, e.g. "vs previous 30 days". */
  comparisonLabel: string
  ordering: OrganicProductOrdering
  onOrderingChange: (next: OrganicProductOrdering) => void
}) {
  if (data === undefined) {
    return null
  }

  // Marked from the RESPONSE, not from the requested ordering: while a sort
  // refetches, the rows on screen are still the previous column's, and an arrow
  // that moved ahead of them would label them wrongly for that beat.
  const active = { sort: data.sort, direction: data.direction }
  // First click on a column ranks it best-first; clicking the live column flips
  // it. Ascending is how the weakest rows are found, which is a real question
  // here — a product that fell off a cliff sorts to the top of trend-ascending.
  const onSort = (sort: OrganicProductSort) =>
    onOrderingChange({
      sort,
      direction:
        ordering.sort === sort && ordering.direction === "desc" ? "asc" : ("desc" as const),
    })

  return (
    <section className="border-t border-border/70 pt-5 sm:pt-6">
      <div className="flex flex-col gap-1 px-5 sm:flex-row sm:items-baseline sm:justify-between sm:px-6">
        <div className="text-[15px] font-semibold tracking-tight text-foreground">
          Organic products
        </div>

        <p className="text-xs text-muted-foreground">{caption(data)}</p>
      </div>

      {data.rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground sm:px-6">
          No organic sales in this period.
        </p>
      ) : (
        <>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs text-muted-foreground">
                  {/* Both left columns freeze: the row is unreadable once its
                      name scrolls away, and the link is what the column is for. */}
                  <th
                    className="sticky left-0 z-10 w-12 min-w-12 bg-card py-2 pl-5 pr-2 text-left font-normal sm:pl-6"
                    title="Open the product's page on this marketplace"
                  >
                    Link
                  </th>

                  <th className="sticky left-12 z-10 w-64 min-w-64 bg-card py-2 pr-3 text-left font-normal">
                    Product
                  </th>

                  <SortHeader
                    label="Organic revenue"
                    title="Line revenue left after the period's ad-attributed revenue"
                    sort={ORGANIC_PRODUCT_SORTS.revenue}
                    active={active}
                    onSort={onSort}
                  />

                  <SortHeader
                    label="Organic units"
                    title="Units sold left after the period's attributed conversions"
                    sort={ORGANIC_PRODUCT_SORTS.units}
                    active={active}
                    onSort={onSort}
                  />

                  <Th
                    label="Stock"
                    title="On-hand warehouse stock as of the last catalog sync — not live, and not what this marketplace can sell"
                  />

                  <SortHeader
                    label="Trend"
                    title={`${comparisonLabel}. Products with no sales in the previous period carry no percentage and rank last either way.`}
                    sort={ORGANIC_PRODUCT_SORTS.trend}
                    active={active}
                    onSort={onSort}
                  />
                </tr>
              </thead>

              <tbody>
                {data.rows.map((row) => (
                  <Row
                    key={row.sku}
                    row={row}
                    currency={currency}
                    rates={rates}
                    comparisonLabel={comparisonLabel}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 text-xs sm:px-6">
            <Link to="/products" className="text-muted-foreground hover:text-foreground">
              All products →
            </Link>
          </div>
        </>
      )}
    </section>
  )
}

function Row({
  row,
  currency,
  rates,
  comparisonLabel,
}: {
  row: OrganicProductRow
  currency: string
  rates: ExchangeRates | undefined
  comparisonLabel: string
}) {
  const label = row.name ?? row.sku
  return (
    <tr className="group border-b border-border/40 transition-colors last:border-0 hover:bg-table-row-hover">
      <td className="sticky left-0 z-10 w-12 min-w-12 bg-card py-2 pl-5 pr-2 group-hover:bg-table-row-hover sm:pl-6">
        <ListingLink url={row.listingUrl} label={label} />
      </td>

      <td className="sticky left-12 z-10 w-64 min-w-64 bg-card py-2 pr-3 group-hover:bg-table-row-hover">
        <div className="flex items-start gap-3">
          {/* Only the name links inward. The ids below are copy buttons, and a
              button inside an anchor is neither valid nor clickable the way
              either one promises. */}
          <Link to="/products/$sku" params={{ sku: row.sku }} title={label}>
            <ProductThumb url={row.imageUrl} alt={label} />
          </Link>

          <div className="min-w-0">
            {/* The same gesture as the marketplace name in the page header
                (`components/layout/page-header.tsx`): the label underlines and
                the arrow lifts on hover. Its own named group, because the row
                already owns `group` for the hover background. */}
            <Link
              to="/products/$sku"
              params={{ sku: row.sku }}
              title={label}
              className="group/name inline-flex max-w-full items-center gap-1 font-medium text-foreground"
            >
              <span className="truncate underline-offset-4 group-hover/name:underline">
                {label}
              </span>

              <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground/0 transition-all group-hover/name:-translate-y-0.5 group-hover/name:translate-x-0.5 group-hover/name:text-foreground" />
            </Link>

            <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <CopyValue value={row.sku} className="max-w-full truncate" />

              {row.ean === null ? null : (
                <CopyValue value={row.ean} className="max-w-full truncate" />
              )}
            </div>
          </div>
        </div>
      </td>

      <Td>
        <Money
          amount={row.organicRevenue}
          currency={currency}
          rates={rates}
          layout="inline"
          maximumFractionDigits={0}
          className="font-medium text-foreground"
        />

        {row.attributionExceededSales ? <ClampMark /> : null}
      </Td>

      <Td>
        <span className="tabular-nums text-muted-foreground">{formatUnits(row.organicUnits)}</span>
      </Td>

      <Td>
        <StockCell quantity={row.inventoryQty} />
      </Td>

      <Td>
        <span className="inline-flex justify-end">
          <MetricTrend
            current={row.organicRevenue}
            previous={row.previousOrganicRevenue}
            title={comparisonLabel}
          />
        </span>
      </Td>
    </tr>
  )
}

/** Warehouse stock, or a dash when nothing is mirrored for the SKU. The dash is
 *  the point: an unknown quantity rendered as 0 would read as "sold out", which
 *  is a confident claim nobody made. Zero itself is muted — it is real, and it
 *  is the row worth noticing on a product that still sells organically. */
function StockCell({ quantity }: { quantity: number | null }) {
  if (quantity === null) {
    return (
      <span className="text-muted-foreground/40" title="Not mirrored from the catalog yet">
        —
      </span>
    )
  }
  return (
    <span className={cn("tabular-nums", quantity === 0 ? "text-warning" : "text-muted-foreground")}>
      {quantity.toLocaleString()}
    </span>
  )
}

/** A plain right-aligned header, for a column that does not rank. */
function Th({ label, title }: { label: string; title: string }) {
  return (
    <th className="whitespace-nowrap py-2 px-3 text-right font-normal" title={title}>
      {label}
    </th>
  )
}

/** The caption under the heading carries the two things that stop the numbers
 *  from being misread: what the revenue column IS, and whether anything was
 *  actually subtracted from it. */
function caption(data: OrganicProductsResponse): string {
  const basis = "Line revenue, tax excluded — does not sum to the GMV above."
  return data.adReported
    ? `Ad-attributed sales removed. ${basis}`
    : `No ad report for this period, so every sale counts as organic. ${basis}`
}

/** A SKU the window attributed to ads more heavily than it sold. The figure
 *  beside this mark is clamped at zero, not measured — attribution is dated to
 *  the click and the sale to the order, so a short window can do that. */
function ClampMark() {
  return (
    <span
      className="ml-1 cursor-default text-muted-foreground/50"
      title="Ads were attributed the whole period's sales for this product, so the figure is floored at zero"
    >
      *
    </span>
  )
}

/** Units come out of the subtraction fractional (attributed conversions are),
 *  so a whole number prints whole and a remainder keeps one decimal. */
function formatUnits(units: number): string {
  return units.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

function ProductThumb({ url, alt }: { url: string | null; alt: string }) {
  if (!url) {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/40 text-muted-foreground/40">
        <Package className="size-4" />
      </div>
    )
  }
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="size-8 shrink-0 rounded-md border border-border/60 bg-white object-contain"
    />
  )
}

function SortHeader({
  label,
  title,
  sort,
  active,
  onSort,
}: {
  label: string
  title: string
  sort: OrganicProductSort
  active: OrganicProductOrdering
  onSort: (sort: OrganicProductSort) => void
}) {
  const isActive = active.sort === sort
  return (
    <th
      aria-sort={ariaSort(isActive, active.direction)}
      className="whitespace-nowrap py-2 px-3 text-right font-normal"
      title={title}
    >
      <button
        type="button"
        onClick={() => onSort(sort)}
        className={cn(
          "ml-auto inline-flex items-center gap-1 transition-colors hover:text-foreground",
          isActive && "text-foreground"
        )}
      >
        {label}

        <SortArrow active={isActive} direction={active.direction} />
      </button>
    </th>
  )
}

/** The marketplace's own product page. Not sortable, and not merged into the
 *  product cell: that one already links INWARD to `/products/$sku`, and a single
 *  cell with two destinations is a cell nobody can click confidently.
 *
 *  A dash rather than a disabled icon when the marketplace config carries no
 *  `productUrl` template — no marketplace has one today, so an icon that never
 *  works would read as broken rather than as unconfigured. */
function ListingLink({ url, label }: { url: string | null; label: string }) {
  if (url === null) {
    return <span className="text-muted-foreground/40">—</span>
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={`Open ${label} on this marketplace`}
      className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
    >
      <ExternalLink className="size-3.5" />

      <span className="sr-only">{`Open ${label} on this marketplace`}</span>
    </a>
  )
}

function ariaSort(
  active: boolean,
  direction: OrganicProductSortDirection
): "ascending" | "descending" | "none" {
  if (!active) {
    return "none"
  }
  return direction === "desc" ? "descending" : "ascending"
}

function SortArrow({
  active,
  direction,
}: {
  active: boolean
  direction: OrganicProductSortDirection
}) {
  // An inactive column keeps an invisible arrow so the header row does not
  // change width when the ranking moves to another column.
  if (!active) {
    return <ArrowDown className="size-3 opacity-0" />
  }
  return direction === "desc" ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap py-2 px-3 text-right">{children}</td>
}
